import { useEffect, useMemo, useState } from 'react';
import { RaeditorApp, createStore, setTranslations, setApiEndpoints } from '@reyka/openpolotno';
import { englishTranslations } from './i18n/en';
import { TopBar, STORAGE_KEY, createBlankDesign } from './components/TopBar';
import { ensureStoreFonts, normalizeTextLetterSpacing, patchStoreLoadJsonFonts } from './ensureStoreFonts';
import {
  startTemplateHoverTitles,
  TEMPLATE_PANEL_STATUS_EVENT,
} from './templateHoverTitles';

/**
 * Full editor sections. Stock photos, templates, fonts, and AI go through
 * the self-hosted Cloudflare Worker (see worker/) — never api.polotno.com.
 */
const SECTIONS = [
  'templates',
  'text',
  'photos',
  'elements',
  'upload',
  'background',
  'layers',
  'history',
  'size',
] as const;

// Point OpenPolotno HTTP clients at our Worker (Vite proxies /api → :8787 in dev).
// Must use setApiEndpoints from the same main entry as RaeditorApp (patched exports).
setApiEndpoints({
  urlBase: import.meta.env.VITE_API_URL || window.location.origin,
  apiPathPrefix: 'api',
  // Client builds font preview URLs from urlBase (no api prefix) by default;
  // force them under /api so the Vite proxy / Worker route serves them.
  paths: {
    googleFontImage: 'api/google-fonts-previews/black',
  },
});

// Apply English before any editor UI mounts.
setTranslations(englishTranslations as unknown as Record<string, unknown>);

function createEditorStore() {
  const store = createStore({
    key: (import.meta.env.VITE_OPENPOLOTNO_KEY || '').trim() || 'self-hosted',
    showCredit: false,
  });

  store.setAiToolsEnabled?.(true);
  patchStoreLoadJsonFonts(store);

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      store.loadJSON(JSON.parse(raw));
    } else {
      createBlankDesign(store);
    }
  } catch {
    createBlankDesign(store);
  }

  normalizeTextLetterSpacing(store);
  void ensureStoreFonts(store);
  return store;
}

export default function App() {
  const store = useMemo(() => createEditorStore(), []);
  const [status, setStatus] = useState(
    'Self-hosted Worker backend — photos, templates, fonts & AI via /api (not api.polotno.com)',
  );

  useEffect(() => {
    const handle = startTemplateHoverTitles();
    return () => handle.stop();
  }, []);

  useEffect(() => {
    const onStatusEvent = (ev: Event) => {
      const detail = (ev as CustomEvent<{ message?: string }>).detail;
      if (detail?.message) setStatus(detail.message);
    };
    window.addEventListener(TEMPLATE_PANEL_STATUS_EVENT, onStatusEvent);
    return () => window.removeEventListener(TEMPLATE_PANEL_STATUS_EVENT, onStatusEvent);
  }, []);

  return (
    <div className="app-shell">
      <TopBar store={store} status={status} onStatus={setStatus} />
      <main className="editor-frame">
        <RaeditorApp store={store} sections={[...SECTIONS]} />
      </main>
    </div>
  );
}
